import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import theme from "../theme";
import PasswordApi from "../apis/passwordApi";

// Survive React remounts (e.g. LaunchDarklyProvider key changes when Auth0
// finishes loading) so we do not re-fetch after the secret was already marked
// viewed. Intentionally module-scoped: a full page reload still enforces
// one-time semantics via the API.
const revealedSecrets = new Map<string, string>();
const markedViewedKeys = new Set<string>();

const Password: React.FC = () => {
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [alreadyViewed, setAlreadyViewed] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const markViewed = async (secretKey: string) => {
      if (markedViewedKeys.has(secretKey)) {
        return;
      }
      markedViewedKeys.add(secretKey);
      try {
        await PasswordApi.markAsViewed(secretKey);
      } catch (markError) {
        // Allow a later remount to retry if the request never landed.
        markedViewedKeys.delete(secretKey);
        console.error("Failed to mark secret as viewed:", markError);
      }
    };

    const fetchSecret = async () => {
      if (!key) {
        setError("No key provided in the URL");
        setLoading(false);
        return;
      }

      const cached = revealedSecrets.get(key);
      if (cached) {
        setSecret(cached);
        setMessage("This link has now expired and cannot be viewed again.");
        setLoading(false);
        void markViewed(key);
        return;
      }

      try {
        const response = await PasswordApi.getSecret(key);

        if (cancelled) {
          return;
        }

        if (response.success) {
          revealedSecrets.set(key, response.value);
          setSecret(response.value);
          setMessage(response.message || "This link has now expired and cannot be viewed again.");
          void markViewed(key);
        } else if (response.alreadyViewed) {
          setAlreadyViewed(true);
          setMessage(response.message || "This secret has already been viewed and can no longer be accessed.");
        } else {
          setError(response.message || response.error || "Secret not found");
        }
      } catch (err: any) {
        if (cancelled) {
          return;
        }

        console.error("Error fetching secret:", err);
        setError(
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "An error occurred while retrieving the secret"
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSecret();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        background: theme.palette.background.default,
        pt: 3,
        px: 3,
        pb: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 600,
          width: "100%",
          p: 4,
          borderRadius: 3,
          border: "1px solid #E5E7EB",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: "var(--theo-font-sans)",
                fontSize: "14px",
              }}
            >
              Loading...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center" }}>
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                fontFamily: "var(--theo-font-sans)",
                "& .MuiAlert-message": {
                  textAlign: "center",
                  width: "100%",
                },
              }}
            >
              {error}
            </Alert>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: "var(--theo-font-sans)",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              The link you followed may be invalid or expired.
            </Typography>
          </Box>
        ) : alreadyViewed ? (
          <Box sx={{ textAlign: "center" }}>
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: 2,
                fontFamily: "var(--theo-font-sans)",
                "& .MuiAlert-message": {
                  textAlign: "center",
                  width: "100%",
                },
              }}
            >
              {message}
            </Alert>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: "var(--theo-font-sans)",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              For security reasons, one-time secrets can only be viewed once.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: "18px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 3,
                textAlign: "center",
              }}
            >
              One-Time Secret
            </Typography>

            <Box
              sx={{
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 2,
                p: 3,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              >
                Secret Value:
              </Typography>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  wordBreak: "break-all",
                  whiteSpace: "pre-wrap",
                }}
              >
                {secret}
              </Typography>
            </Box>

            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                fontFamily: "var(--theo-font-sans)",
              }}
            >
              {message}
            </Alert>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Password;
